import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { displayDateAndTime, INTERNAL_USER_NAME, parseSafeJSON } from "Backend";
import CopyToClipboard from "components/CopyToClipboard";
import { useScriptsHistoryLoader } from "loaders/scripts.loader";
import { PopoverCell } from "./ScriptRoute";
import { parseAppUserPuid } from "puid";
import CaseAutocomplete from "components/CaseAutocomplete";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { Navigate, useParams } from "react-router";
import PDatePicker from "components/PDatePicker";
import { add } from "date-fns";
import { Link } from "react-router-dom";
import { AppRoutes } from "AppRoutes";
import { ArrowDropDown } from "@mui/icons-material";
import { FullName } from "components/FullName";
import React, { useState } from "react";
import { LensScriptRunView } from "generated/graphql";

function CreatorCell({ puid }: { puid: string }) {
  const appUserId = parseAppUserPuid(puid);

  return (
    <TableCell>
      <FullName userId={appUserId} defaultName={INTERNAL_USER_NAME} />
    </TableCell>
  );
}

export function ScriptsHistory() {
  const scriptRuns = useScriptsHistoryLoader();
  const { updateParam, searchParams } = useSearchParamsWithUpdate();
  const { lensId } = useParams();
  const minDateParam = searchParams.get("minDate");
  const maxDateParam = searchParams.get("maxDate");
  const minDate = minDateParam ? new Date(minDateParam) : undefined;
  const maxDate = maxDateParam ? new Date(maxDateParam) : undefined;
  const [showDialogForRun, setShowDialogForRun] = useState<LensScriptRunView | undefined>(undefined);

  const displayActions = (actionsString: string) => {
    if (actionsString) {
      const actionsAsArray = parseSafeJSON<any[]>(actionsString);
      return (
        actionsAsArray && (
          <TableContainer>
            <Table>
              <TableBody>
                {actionsAsArray.map((actionInfo, index) => (
                  <TableRow key={index}>
                    <TableCell>{JSON.stringify(actionInfo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      );
    } else {
      return <p>N/A</p>;
    }
  };

  const displayCalcTable = (calcProps: string) => {
    if (calcProps) {
      const propsAsObject = parseSafeJSON<Record<string, unknown>[]>(calcProps);
      const tableElements: React.JSX.Element[] = [];
      for (const key in propsAsObject) {
        tableElements.push(
          <TableRow key={key}>
            <TableCell>{key}</TableCell>
            <TableCell>{propsAsObject[key] ? propsAsObject[key].value : "null"}</TableCell>
          </TableRow>
        );
      }
      return (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{tableElements}</TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return <p>N/A</p>;
    }
  };

  const displayPropTable = (displayProps: string) => {
    if (displayProps) {
      const propsAsObject = parseSafeJSON<Record<string, unknown>[]>(displayProps);
      const tableElements: React.JSX.Element[] = [];
      for (const key in propsAsObject) {
        tableElements.push(
          <TableRow key={key}>
            <TableCell>{key}</TableCell>
            <TableCell>{propsAsObject[key]}</TableCell>
          </TableRow>
        );
      }
      return (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{tableElements}</TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return <p>N/A</p>;
    }
  };

  if (!lensId) {
    return <Navigate to=".." />;
  }

  return (
    <Box sx={{ pt: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", py: 1, gap: 1 }}>
        <CaseAutocomplete
          label="Case"
          lensId={parseInt(lensId)}
          onChange={(lensCase) => {
            updateParam("caseId", lensCase?.caseId.toString() ?? undefined);
          }}
        />
        <PDatePicker
          label="Start Date"
          date={minDate}
          setDate={(date) => {
            updateParam("minDate", date ? date.toISOString() : undefined);
          }}
        />
        <PDatePicker
          label="End Date"
          date={maxDate}
          setDate={(date) => {
            const maxDate = date ? add(date, { days: 1 }).toISOString() : undefined;
            updateParam("maxDate", maxDate);
          }}
        />
      </Box>
      <TableContainer sx={{ height: "100%" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Run ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Case ID</TableCell>
              <TableCell sx={{ display: "flex", alignItems: "center" }}>
                Date <ArrowDropDown />
              </TableCell>
              <TableCell>Action Types</TableCell>
              <TableCell>Failure Reason</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scriptRuns.map((run) => (
              <TableRow key={run.id}>
                <TableCell>
                  <CopyToClipboard text={run.batchId} />
                </TableCell>
                <TableCell>{run.scriptName}</TableCell>
                <TableCell>
                  <Link to={AppRoutes.caseDetailBuilder(parseInt(lensId), run.caseId)} target="_blank">
                    {run.caseId}
                  </Link>
                </TableCell>
                <TableCell>{displayDateAndTime(new Date(run.createdAt))}</TableCell>
                <PopoverCell output={run.actionTypes.join("\n")} displayText={run.actionTypes.at(0)} />
                <TableCell>{run.failureReason}</TableCell>
                <CreatorCell puid={run.createdByPuid} />
                <TableCell onClick={() => setShowDialogForRun(run)}>
                  <p className="text-blue-600 underline cursor-pointer">More</p>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showDialogForRun && (
        <Dialog maxWidth="lg" open={true} onClose={() => setShowDialogForRun(undefined)}>
          <DialogTitle>Detailed Script Run Info</DialogTitle>
          <DialogContent>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Calcs</TableCell>
                  <TableCell>Properties</TableCell>
                  <TableCell>Std Output</TableCell>
                  <TableCell>Detailed Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ verticalAlign: "top" }}>
                <TableCell>{displayCalcTable(showDialogForRun.calcsJson)}</TableCell>
                <TableCell>{displayPropTable(showDialogForRun.propertiesJson)}</TableCell>
                <TableCell>{showDialogForRun.stdOutput}</TableCell>
                <TableCell>{displayActions(showDialogForRun.actionsJson)}</TableCell>
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
